import { useMemo } from "react";
import { useGetProfileQuery } from "../../redux/reducers/apiSlice";

export const FREE_TIER_LIMITS = {
  MAX_SPARKS: 3,
  MAX_METRICS_PER_SPARK: 3,
  MAX_PROGRAMS: 1,
  MAX_FUNDERS: 1,
  MAX_ORGANIZATIONS: 1,
};

export const useTierLimits = () => {
  const { data: profileData } = useGetProfileQuery();
  
  const userTier = profileData?.subscription_tier || "FREE";
  const isPaidTier = userTier === "PAID";
  
  const organization = profileData?.organizations?.[0];
  const organizationCount = profileData?.organizations?.length ?? 0;

  const tierInfo = useMemo(() => {
    if (isPaidTier) {
      return {
        tier: "PAID",
        isPaid: true,
        limits: {
          sparks: { current: organization?.sparks?.length || 0, max: Infinity, canAdd: true },
          metricsPerSpark: { current: 0, max: Infinity, canAdd: true },
          programs: { current: 0, max: Infinity, canAdd: true },
          funders: { current: 0, max: Infinity, canAdd: true },
          organizations: { current: organizationCount, max: Infinity, canAdd: true },
        },
        upgradeMessage: null,
      };
    }

    // Free tier limits
    const sparkCount = organization?.sparks?.length || 0;
    const canAddSpark = sparkCount < FREE_TIER_LIMITS.MAX_SPARKS;
    const canAddOrganization = organizationCount < FREE_TIER_LIMITS.MAX_ORGANIZATIONS;

    return {
      tier: "FREE",
      isPaid: false,
      limits: {
        sparks: {
          current: sparkCount,
          max: FREE_TIER_LIMITS.MAX_SPARKS,
          canAdd: canAddSpark,
        },
        metricsPerSpark: {
          current: 0, // Will be calculated per spark
          max: FREE_TIER_LIMITS.MAX_METRICS_PER_SPARK,
          canAdd: true, // Will be checked per spark
        },
        programs: {
          current: 0, // Will be fetched from API
          max: FREE_TIER_LIMITS.MAX_PROGRAMS,
          canAdd: true, // Will be checked when creating
        },
        funders: {
          current: 0, // Will be checked when creating report
          max: FREE_TIER_LIMITS.MAX_FUNDERS,
          canAdd: true, // Will be checked when creating
        },
        organizations: {
          current: organizationCount,
          max: FREE_TIER_LIMITS.MAX_ORGANIZATIONS,
          canAdd: canAddOrganization,
        },
      },
      upgradeMessage: canAddSpark
        ? null
        : `Free tier limit reached: ${FREE_TIER_LIMITS.MAX_SPARKS} Sparks max. Upgrade to paid tier for unlimited Sparks.`,
    };
  }, [isPaidTier, organization, organizationCount]);
  
  return tierInfo;
};
